import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTree } from '@/hooks/useTree';
import { geocodePlaces, type GeoResult } from '@/utils/geocode';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { TreeNode } from '@/api/tree.api';

// ─── Typen ──────────────────────────────────────────────────────────────────

interface MapMarker {
  lat: number;
  lng: number;
  place: string;
  events: MarkerEvent[];
}

interface MarkerEvent {
  type: 'birth' | 'death' | 'marriage';
  label: string;
  persons: string[];
  date?: string | null;
  year?: number | null;
}

// ─── Historische Ereignisse ─────────────────────────────────────────────────

interface HistoricalEvent {
  year: number;
  lat: number;
  lng: number;
  title: string;
  city: string;
  color: string;
}

const HISTORICAL_EVENTS: HistoricalEvent[] = [
  { year: 1618, lat: 50.09, lng: 14.42, title: 'Dreißigjähriger Krieg beginnt', city: 'Prag', color: '#7f1d1d' },
  { year: 1648, lat: 51.97, lng: 7.61, title: 'Westfälischer Friede', city: 'Münster/Osnabrück', color: '#1e3a8a' },
  { year: 1789, lat: 48.86, lng: 2.35, title: 'Französische Revolution', city: 'Paris', color: '#1d4ed8' },
  { year: 1806, lat: 48.57, lng: 12.15, title: 'Ende des Heiligen Röm. Reichs', city: 'Regensburg', color: '#92400e' },
  { year: 1813, lat: 51.34, lng: 12.37, title: 'Völkerschlacht bei Leipzig', city: 'Leipzig', color: '#dc2626' },
  { year: 1815, lat: 48.20, lng: 16.37, title: 'Wiener Kongress', city: 'Wien', color: '#0369a1' },
  { year: 1848, lat: 50.11, lng: 8.68, title: 'Frankfurter Nationalversammlung', city: 'Frankfurt', color: '#7c3aed' },
  { year: 1871, lat: 48.80, lng: 2.12, title: 'Reichsgründung', city: 'Versailles', color: '#4c1d95' },
  { year: 1914, lat: 52.52, lng: 13.40, title: '1. Weltkrieg beginnt', city: 'Berlin', color: '#b91c1c' },
  { year: 1918, lat: 52.52, lng: 13.40, title: 'Ende 1. Weltkrieg / Weimarer Republik', city: 'Berlin', color: '#065f46' },
  { year: 1933, lat: 52.52, lng: 13.40, title: 'Machtergreifung NSDAP', city: 'Berlin', color: '#7f1d1d' },
  { year: 1939, lat: 52.23, lng: 21.01, title: '2. Weltkrieg beginnt', city: 'Warschau', color: '#b91c1c' },
  { year: 1945, lat: 52.52, lng: 13.40, title: 'Kriegsende / Teilung Deutschlands', city: 'Berlin', color: '#374151' },
  { year: 1961, lat: 52.52, lng: 13.40, title: 'Mauerbau Berlin', city: 'Berlin', color: '#374151' },
  { year: 1989, lat: 52.52, lng: 13.40, title: 'Mauerfall', city: 'Berlin', color: '#065f46' },
];

// ─── Farben pro Event-Typ ───────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = {
  birth: '#22c55e',   // grün
  death: '#ef4444',   // rot
  marriage: '#a855f7', // lila
};

const EVENT_LABELS: Record<string, string> = {
  birth: 'Geburt',
  death: 'Tod',
  marriage: 'Heirat',
};

// ─── Hilfsfunktion: Jahr extrahieren ───────────────────────────────────────

function extractYear(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = text.match(/\d{4}/);
  return m ? parseInt(m[0], 10) : null;
}

// ─── Auto-Fit-Komponente ────────────────────────────────────────────────────

function FitBounds({ markers }: { markers: MapMarker[] }) {
  const map = useMap();

  useEffect(() => {
    if (markers.length === 0) return;

    const bounds = markers.map((m) => [m.lat, m.lng] as [number, number]);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
  }, [map, markers]);

  return null;
}

// ─── Kartenkacheln ──────────────────────────────────────────────────────────
//
// EINE Kachelquelle, nicht zwei. Die „historische Karte" ist eine Einfärbung
// derselben Kacheln und kein zweiter Kartendienst.
//
// Vorher lag hinter dem Schalter Stamen Toner Lite, gehostet bei Stadia Maps.
// Das sah historisch aus und funktionierte in der Entwicklung – aber nur dort:
// Stadia lässt `localhost` durch und weist jede nicht registrierte Domain mit
// 401 ab. Nachgemessen am 2026-08-25:
//
//   Referer: localhost           → 200
//   Referer: irgendeine Domain    → 401
//
// Im Betrieb blieb die Karte deshalb für alle leer, und in der Entwicklung
// fiel es niemandem auf. Genau die Sorte Fehler, die man nicht findet, indem
// man es „bei sich" ausprobiert.
//
// Ein zweiter Kartendienst für einen Farbton ist ohnehin zu viel: Die Wirkung
// ist eine Frage der Darstellung, nicht der Daten. Die Einfärbung sitzt jetzt
// in der Kachel-Ebene (siehe `.karte-historisch` in index.css) und kostet
// keine einzige zusätzliche Anfrage – und keine Registrierung bei irgendwem.

function Kacheln() {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
  );
}

// ─── Historische-Ereignisse-Layer ──────────────────────────────────────────

function HistoricalEventsLayer({ events, rangeFrom, rangeTo }: {
  events: HistoricalEvent[];
  rangeFrom: number;
  rangeTo: number;
}) {
  const visible = events.filter((e) => e.year >= rangeFrom && e.year <= rangeTo);
  return (
    <>
      {visible.map((e, i) => (
        <CircleMarker
          key={i}
          center={[e.lat, e.lng]}
          radius={8}
          pathOptions={{
            color: e.color,
            fillColor: e.color,
            fillOpacity: 0.85,
            weight: 2,
          }}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold text-slate-800">{e.title}</p>
              <p className="text-slate-500">{e.year} · {e.city}</p>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

// ─── MapView ────────────────────────────────────────────────────────────────

export function MapView() {
  const { data: treeData, isLoading: isTreeLoading } = useTree();
  const [geoMap, setGeoMap] = useState<Map<string, GeoResult>>(new Map());
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Jahr-Bereich für Filterung
  const [yearMin, setYearMin] = useState<number>(1600);
  const [yearMax, setYearMax] = useState<number>(new Date().getFullYear());
  const [rangeFrom, setRangeFrom] = useState<number>(1600);
  const [rangeTo, setRangeTo]   = useState<number>(new Date().getFullYear());

  // Feature-Toggles
  const [showEvents, setShowEvents]         = useState(false);
  const [showHistoricMap, setShowHistoricMap] = useState(false);

  // Alle Orte aus den Daten extrahieren
  const allPlaces = useMemo(() => {
    if (!treeData) return [];
    const places = new Set<string>();

    for (const node of treeData.nodes) {
      if (node.birthPlace) places.add(node.birthPlace);
      if (node.deathPlace) places.add(node.deathPlace);
    }
    for (const link of treeData.links) {
      if (link.startPlace) places.add(link.startPlace);
    }

    return [...places];
  }, [treeData]);

  // Koordinaten holen. Seit die Auflösung auf dem Server liegt, ist das EINE
  // Anfrage statt einer je Ort – kein Fortschrittsbalken mehr nötig, weil es
  // nichts mehr zu warten gibt.
  useEffect(() => {
    if (allPlaces.length === 0) return;

    let cancelled = false;
    setIsGeocoding(true);

    geocodePlaces(allPlaces)
      .then((results) => {
        if (!cancelled) setGeoMap(results);
      })
      .finally(() => {
        if (!cancelled) setIsGeocoding(false);
      });

    return () => { cancelled = true; };
  }, [allPlaces]);

  // Orte, zu denen der Server (noch) keine Koordinaten hat. Sie fehlen auf der
  // Karte, und wer das nicht weiß, hält die Karte für vollständig.
  const ohneKoordinaten = allPlaces.filter((p) => !geoMap.has(p)).length;

  // Marker aus den Daten + Geocoding-Ergebnissen bauen
  const markers = useMemo(() => {
    if (!treeData || geoMap.size === 0) return [];

    // Personen-Map für schnellen Zugriff
    const personMap = new Map<string, TreeNode>();
    for (const node of treeData.nodes) {
      personMap.set(node.id, node);
    }

    // Events pro Ort sammeln
    const placeEvents = new Map<string, MarkerEvent[]>();

    function addEvent(place: string, event: MarkerEvent) {
      if (!geoMap.has(place)) return;
      const existing = placeEvents.get(place) ?? [];
      existing.push(event);
      placeEvents.set(place, existing);
    }

    // Geburten & Tode
    for (const node of treeData.nodes) {
      const name = `${node.firstName} ${node.lastName ?? ''}`.trim();

      if (node.birthPlace) {
        addEvent(node.birthPlace, {
          type: 'birth',
          label: 'Geburt',
          persons: [name],
          date: node.birthDateText,
          year: extractYear(node.birthDateText),
        });
      }

      if (node.deathPlace) {
        addEvent(node.deathPlace, {
          type: 'death',
          label: 'Tod',
          persons: [name],
          date: node.deathDateText,
          year: extractYear(node.deathDateText),
        });
      }
    }

    // Heiraten
    for (const link of treeData.links) {
      if (!link.startPlace) continue;
      const p1 = personMap.get(link.person1Id);
      const p2 = personMap.get(link.person2Id);
      if (!p1 || !p2) continue;

      const name1 = `${p1.firstName} ${p1.lastName ?? ''}`.trim();
      const name2 = `${p2.firstName} ${p2.lastName ?? ''}`.trim();

      addEvent(link.startPlace, {
        type: 'marriage',
        label: link.type === 'married' ? 'Heirat' : 'Partnerschaft',
        persons: [name1, name2],
        year: null,
      });
    }

    // Zu Markern zusammenbauen
    const result: MapMarker[] = [];
    for (const [place, events] of placeEvents) {
      const geo = geoMap.get(place);
      if (!geo) continue;

      result.push({
        lat: geo.lat,
        lng: geo.lng,
        place,
        events,
      });
    }

    return result;
  }, [treeData, geoMap]);

  // Jahres-Min/Max aus Marker-Daten berechnen
  const { dataYearMin, dataYearMax } = useMemo(() => {
    let min = 9999, max = -9999;
    for (const m of markers) {
      for (const e of m.events) {
        if (e.year) {
          if (e.year < min) min = e.year;
          if (e.year > max) max = e.year;
        }
      }
    }
    if (min === 9999) min = 1600;
    if (max === -9999) max = new Date().getFullYear();
    return { dataYearMin: min, dataYearMax: max };
  }, [markers]);

  // Bereich initialisieren wenn Daten geladen
  useEffect(() => {
    setYearMin(dataYearMin);
    setYearMax(dataYearMax);
    setRangeFrom(dataYearMin);
    setRangeTo(dataYearMax);
  }, [dataYearMin, dataYearMax]);

  // Marker nach Jahresbereich filtern
  const filteredMarkers = useMemo(() => {
    return markers.map((m) => {
      const filteredEvents = m.events.filter(
        (e) => e.year == null || (e.year >= rangeFrom && e.year <= rangeTo),
      );
      return filteredEvents.length > 0 ? { ...m, events: filteredEvents } : null;
    }).filter((m): m is MapMarker => m !== null);
  }, [markers, rangeFrom, rangeTo]);

  // Radius basierend auf Anzahl Events
  function markerRadius(marker: MapMarker): number {
    return Math.min(6 + marker.events.length * 2, 20);
  }

  // Dominante Farbe (meiste Events des Typs)
  function markerColor(marker: MapMarker): string {
    const counts: Record<string, number> = {};
    for (const e of marker.events) {
      counts[e.type] = (counts[e.type] ?? 0) + 1;
    }
    const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return EVENT_COLORS[dominant[0]] ?? '#3b82f6';
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (isTreeLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" message="Stammbaum wird geladen..." />
      </div>
    );
  }

  if (!treeData || treeData.nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Keine Personen vorhanden.
      </div>
    );
  }

  if (allPlaces.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Keine Ortsdaten vorhanden. Orte bei Geburt, Tod oder Heirat eintragen, um die Karte zu nutzen.
      </div>
    );
  }

  const sliderRange = yearMax - yearMin || 1;

  return (
    <div className={`relative h-full w-full${showHistoricMap ? ' karte-historisch' : ''}`}>
      {/* Die drei Kästchen oben links stehen in EINER Spalte. Einzeln mit festem
          Abstand positioniert, überdeckten sie sich, sobald das Overlay-Panel
          höher war als der angenommene Platz. Der Wrapper lässt Klicks auf die
          Karte durch; nur die Kästchen selbst fangen sie ab. */}
      <div className="pointer-events-none absolute left-4 top-4 z-[1000] flex max-w-xs flex-col items-start gap-2 [&>*]:pointer-events-auto">
      {/* Fehlende Orte: die Karte zeigt sonst weniger, als der Baum hergibt –
          und niemand sieht, dass etwas fehlt. */}
      {!isGeocoding && ohneKoordinaten > 0 && (
        <div className="max-w-xs rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-sm">
          {ohneKoordinaten === 1
            ? 'Ein Ort ist noch nicht zugeordnet und fehlt auf der Karte.'
            : `${ohneKoordinaten} Orte sind noch nicht zugeordnet und fehlen auf der Karte.`}{' '}
          <Link to="/orte" className="font-medium underline hover:text-amber-950">
            Ortsliste öffnen
          </Link>
        </div>
      )}

      {/* Ladehinweis */}
      {isGeocoding && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Orte werden geladen …
          </div>
        </div>
      )}

      {/* Overlay-Steuerung (oben links) */}
      {!isGeocoding && (
        <div className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white/95 px-3 py-2.5 shadow-lg backdrop-blur-sm text-xs">
          <p className="mb-0.5 font-semibold text-slate-600 text-[10px] uppercase tracking-wide">Overlays</p>
          <label className="flex cursor-pointer items-center gap-2 text-slate-700">
            <input
              type="checkbox"
              checked={showHistoricMap}
              onChange={(e) => setShowHistoricMap(e.target.checked)}
              className="h-3.5 w-3.5 accent-slate-600"
            />
            Karte historisch einfärben
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-slate-700">
            <input
              type="checkbox"
              checked={showEvents}
              onChange={(e) => setShowEvents(e.target.checked)}
              className="h-3.5 w-3.5 accent-indigo-500"
            />
            Historische Ereignisse
          </label>
        </div>
      )}
      </div>

      {/* Legende */}
      <div className="absolute bottom-4 right-4 z-[1000] rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex flex-col gap-1 text-xs">
          {Object.entries(EVENT_LABELS).map(([type, label]) => (
            <div key={type} className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: EVENT_COLORS[type] }}
              />
              {label}
            </div>
          ))}
          <div className="mt-1 border-t border-slate-100 pt-1 text-slate-400">
            {filteredMarkers.length} Orte / {allPlaces.length} gesamt
          </div>
        </div>
      </div>

      {/* Jahresbereich-Slider (unten mittig) */}
      <div
        className="absolute bottom-20 left-1/2 z-[1000] -translate-x-1/2 rounded-xl border border-slate-200 bg-white/95 px-5 py-3 shadow-lg backdrop-blur-sm"
        style={{ minWidth: 320 }}
      >
        <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
          <span>Zeitraum</span>
          <span className="font-mono">{rangeFrom} – {rangeTo}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-400">{yearMin}</span>
          <div className="relative flex-1">
            {/* Zwei überlagerte Range-Inputs für Dual-Handle-Slider */}
            <input
              type="range"
              min={yearMin}
              max={yearMax}
              value={rangeFrom}
              onChange={(e) => setRangeFrom(Math.min(Number(e.target.value), rangeTo - 1))}
              className="absolute inset-0 h-1 w-full cursor-pointer appearance-none bg-transparent accent-blue-500"
              style={{ zIndex: rangeFrom > (yearMin + yearMax) / 2 ? 5 : 3 }}
            />
            <input
              type="range"
              min={yearMin}
              max={yearMax}
              value={rangeTo}
              onChange={(e) => setRangeTo(Math.max(Number(e.target.value), rangeFrom + 1))}
              className="absolute inset-0 h-1 w-full cursor-pointer appearance-none bg-transparent accent-blue-500"
              style={{ zIndex: 4 }}
            />
            {/* Track-Balken (nur visuell) */}
            <div className="pointer-events-none relative h-1 rounded-full bg-slate-200">
              <div
                className="absolute h-full rounded-full bg-blue-400"
                style={{
                  left: `${((rangeFrom - yearMin) / sliderRange) * 100}%`,
                  right: `${100 - ((rangeTo - yearMin) / sliderRange) * 100}%`,
                }}
              />
            </div>
          </div>
          <span className="text-[10px] text-slate-400">{yearMax}</span>
        </div>
      </div>

      {/* Karte */}
      {/* Die Einfärbung hängt am UMSCHLIESSENDEN Element und nicht an der Karte
          selbst. react-leaflet gibt `className` nur beim Erzeugen des
          Containers weiter – ein späteres Umschalten kam dort nie an. Gemessen:
          Der Haken saß, die Klasse fehlte, die Karte blieb bunt. */}
      <MapContainer
        center={[50.0, 10.0]}
        zoom={6}
        className="h-full w-full"
        style={{ background: '#f1f5f9' }}
        zoomControl={false}
      >
        {/* Unten links: oben links steht die Kästchen-Spalte darüber. */}
        <ZoomControl position="bottomleft" />
        <Kacheln />

        <FitBounds markers={filteredMarkers} />

        {showEvents && (
          <HistoricalEventsLayer
            events={HISTORICAL_EVENTS}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
          />
        )}

        {filteredMarkers.map((marker, i) => (
          <CircleMarker
            key={`${marker.lat}-${marker.lng}-${i}`}
            center={[marker.lat, marker.lng]}
            radius={markerRadius(marker)}
            pathOptions={{
              color: markerColor(marker),
              fillColor: markerColor(marker),
              fillOpacity: 0.6,
              weight: 2,
            }}
          >
            <Popup>
              <div className="max-w-[280px]">
                <h3 className="mb-2 font-semibold text-slate-800">{marker.place}</h3>
                <div className="space-y-1.5">
                  {marker.events.map((event, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm">
                      <span
                        className="mt-1 inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: EVENT_COLORS[event.type] }}
                      />
                      <div>
                        <span className="font-medium">{event.label}:</span>{' '}
                        {event.persons.join(' & ')}
                        {event.date && (
                          <span className="ml-1 text-slate-500">({event.date})</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
